import { useContext, useState, useEffect } from 'react';
import { Eye, Search, X, History } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import adminAuditService from '../../../../../../services/audit_services/adminAuditService';
import Pagination from '../../../../../../components/Pagination';

export default function AdminAuditPlansList() {
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();
  const [searchParams] = useSearchParams();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [programFilter, setProgramFilter] = useState(searchParams.get('program') || '');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [releasedFilter, setReleasedFilter] = useState('');
  const [ordering, setOrdering] = useState('created_at_desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    // Fetch data on initial mount
    fetchPlans();
  }, []);

  useEffect(() => {
    // Fetch when page changes
    if (currentPage > 1) {
      fetchPlans();
    }
  }, [currentPage]);

  const fetchPlans = async () => {
    setLoading(true);
    
    const params = {
      search: searchTerm,
      status: statusFilter,
      program: programFilter,
      is_published: publishedFilter,
      ordering: ordering,
      page: currentPage,
      page_size: pageSize
    };
    
    const result = await adminAuditService.getAuditPlans(authTokens.access, params);
    
    if (result.success) {
      console.log('Plans data:', result.data);
      setPlans(result.data.results || []);
      setTotalPages(result.data.total_pages || 1);
      setTotalCount(result.data.count || 0);
    } else {
      console.error('Error fetching plans:', result.error);
      showError(result.error || 'Erreur lors du chargement des plans d\'audit');
      setPlans([]);
    }
    
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPlans();
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchPlans();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setReleasedFilter('');
    setPublishedFilter('');
    setOrdering('created_at_desc');
    setCurrentPage(1);
    // Fetch with cleared filters
    setTimeout(() => fetchPlans(), 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
      completed: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Complété' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Fermé' },
      achieved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Archivé' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des plans d'audit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Plans d'audit</h1>
        <p className="text-sm text-gray-600 mt-1">Consultez tous les plans d'audit de votre organisation</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rechercher
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                placeholder="Titre, objectifs..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Tous</option>
              <option value="pending">En attente</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Complété</option>
              <option value="closed">Fermé</option>
              <option value="achieved">Archivé</option>
            </select>
          </div>

          {/* Released Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Libération
            </label>
            <select
              value={releasedFilter}
              onChange={(e) => setReleasedFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Tous</option>
              <option value="true">Libéré</option>
              <option value="false">Non libéré</option>
            </select>
          </div>

          {/* Published Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Publication
            </label>
            <select
              value={publishedFilter}
              onChange={(e) => setPublishedFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Tous</option>
              <option value="true">Publié</option>
              <option value="false">Non publié</option>
            </select>
          </div>

          {/* Ordering */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trier par
            </label>
            <select
              value={ordering}
              onChange={(e) => setOrdering(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="created_at_desc">Plus récent</option>
              <option value="created_at_asc">Plus ancien</option>
              <option value="title_asc">Titre (A-Z)</option>
              <option value="title_desc">Titre (Z-A)</option>
            </select>
          </div>
        </div>

        {/* Apply Button */}
        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={handleClearFilters}
            className="inline-flex items-center px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="h-4 w-4 mr-2" />
            Effacer les filtres
          </button>
          <button
            onClick={handleApplyFilters}
            className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Search className="h-4 w-4 mr-2" />
            Appliquer les filtres
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {plans.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Aucun plan d'audit trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Détails du plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Programme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Libération
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Publication
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parties
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Risques
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{plan.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{plan.objectives}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Créé le {new Date(plan.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{plan.program_title}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(plan.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {plan.is_released ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Libéré
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          Non libéré
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {plan.is_published ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Publié
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                          Non publié
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{plan.parties_count || 0}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{plan.risks_count || 0}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/dashboard/admin/audits/plans/${plan.id}`}
                          className="inline-flex items-center px-3 py-1  text-gray-500 text-sm rounded hover:bg-green-200 transition-colors"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                         
                        </Link>
                        <Link
                          to={`/dashboard/admin/audits/plans/${plan.id}/history`}
                          className="inline-flex items-center px-3 py-1  text-gray-500 text-sm rounded hover:bg-green-200 transition-colors"
                        >
                          <History className="h-4 w-4 mr-1" />
                          
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalCount={totalCount}
          pageSize={pageSize}
        />
      )}
    </div>
  );
}
